/**
 * Admin API Service
 * Centralized service for all admin-related API operations
 */

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

class AdminApiService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
  }

  /**
   * Returns auth headers including a fresh Firebase ID token.
   * @returns {Promise<object>}
   */
  async _authHeaders() {
    const { getAuth } = await import('firebase/auth');
    const user = getAuth().currentUser;
    if (!user) {
      throw new Error('Not authenticated');
    }
    const token = await user.getIdToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Build a URL querystring from a params object, omitting null / undefined / '' values.
   * @param {object} params
   * @returns {string} — leading '?' if any params present, else ''
   */
  _buildQuery(params) {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      sp.append(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : '';
  }

  /**
   * Make an HTTP request to the API
   * @param {string} endpoint - API endpoint
   * @param {object} options - Fetch options
   * @returns {Promise<any>} - Response data
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        ...(await this._authHeaders()),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.detail || errorData.message || `HTTP Error: ${response.status}`,
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Network error occurred', 0, error);
    }
  }

  // ==================== User Management Endpoints ====================

  /**
   * Get all users from the database
   * @returns {Promise<{users: Array, total_count: number}>}
   */
  async getAllUsers() {
    return this.request('admin/get_all_users/');
  }

  /**
   * Get a single user by ID
   * @param {string} userId - Firebase UID
   * @returns {Promise<{user: object}>}
   */
  async getUser(userId) {
    return this.request(`admin/get_user/${userId}`);
  }

  /**
   * Update user profile information
   * @param {object} userData - User data to update
   * @param {string} userData.user_id - Firebase UID (required)
   * @param {string} [userData.first_name] - First name
   * @param {string} [userData.last_name] - Last name
   * @param {string} [userData.contact_number] - Contact number
   * @param {string} [userData.customer_logo] - Logo URL
   * @param {string} [userData.primary_account] - Primary account
   * @param {Array} [userData.customers] - Customer array
   * @param {Array} [userData.group] - Group array
   * @returns {Promise<{message: string, user_id: string, updated_fields: Array}>}
   */
  async updateUserProfile(userData) {
    return this.request('admin/update_user_profile/', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Update user's customer assignments
   * @param {object} data
   * @param {string} data.user_id - Firebase UID
   * @param {Array<{customer_id: string, customer_name: string, site_name: string}>} data.customers
   * @returns {Promise<{message: string, user_id: string, customers_count: number}>}
   */
  async updateUserCustomers(data) {
    return this.request('admin/update_user_customers/', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update user's group/supplier assignments
   * @param {object} data
   * @param {string} data.user_id - Firebase UID
   * @param {Array<{group_id: string, group_name: string, site_name: string}>} data.group
   * @returns {Promise<{message: string, user_id: string, groups_count: number}>}
   */
  async updateUserGroups(data) {
    return this.request('admin/update_user_groups/', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Reset a user's password and send email notification
   * @param {string} userId - Firebase UID
   * @returns {Promise<{message: string, user_id: string, email: string}>}
   */
  async resetUserPassword(userId) {
    return this.request('admin/reset_user_password/', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  /**
   * Delete a user from Firebase Auth and Firestore
   * @param {string} userId - Firebase UID
   * @returns {Promise<{message: string, user_id: string}>}
   */
  async deleteUser(userId) {
    return this.request(`admin/delete_user/${userId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Data Endpoints ====================

  /**
   * Get customers by country ID (sorted by site name, then customer name)
   * @param {number} countryId - Country ID (e.g., 190 for UAE)
   * @returns {Promise<Array>} - Response array with customers
   */
  async getCustomersByCountryId(countryId) {
    return this.request(`getCustomersByCountryId/${countryId}`);
  }

  /**
   * Get groups/suppliers by site ID
   * @param {string} siteId - Site ID
   * @returns {Promise<Array>} - Response array with groups
   */
  async getGroupsBySite(siteId) {
    return this.request(`getGroupsBySite/${siteId}`);
  }

  /**
   * Create a new admin account
   * @param {object} accountData - Account data
   * @returns {Promise<any>}
   */
  async createAdminAccount(accountData) {
    return this.request('create_admin_account/', {
      method: 'POST',
      body: JSON.stringify(accountData),
    });
  }

  // ==================== MCS — Sites ====================

  /**
   * List all MCS sites.
   * @returns {Promise<Array>}
   */
  async listSites() {
    return this.request('api/v1/admin/sites');
  }

  /**
   * Get a single MCS site by ID.
   * @param {string|number} id
   * @returns {Promise<object>}
   */
  async getSite(id) {
    return this.request(`api/v1/admin/sites/${id}`);
  }

  /**
   * Create a new MCS site.
   * @param {object} payload - { name, display_name, base_url, api_token, timezone }
   * @returns {Promise<object>}
   */
  async createSite(payload) {
    return this.request('api/v1/admin/sites', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Update an MCS site.
   * @param {string|number} id
   * @param {object} patch - may include api_token (rotation), display_name, base_url, timezone, is_active
   * @returns {Promise<object>}
   */
  async updateSite(id, patch) {
    return this.request(`api/v1/admin/sites/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  /**
   * Test connection to an MCS site.
   * @param {string|number} id
   * @returns {Promise<{ok: boolean, companies?: Array, error?: string}>}
   */
  async testSiteConnection(id) {
    return this.request(`api/v1/admin/sites/${id}/test-connection`, {
      method: 'POST',
    });
  }

  /**
   * Refresh the catalog of MCS customers from the remote MCS site.
   * @param {string|number} id
   * @param {number} [days=90] - 30 | 90 | 180 | 365
   * @returns {Promise<object>}
   */
  async refreshCatalog(id, days = 90) {
    return this.request(`api/v1/admin/sites/${id}/refresh-catalog`, {
      method: 'POST',
      body: JSON.stringify({ days }),
    });
  }

  // ==================== MCS — Customers Catalog ====================

  /**
   * List MCS customers (catalog) with optional filters.
   * @param {object} [filters]
   * @param {string|number|null} [filters.site_id]
   * @param {string|null} [filters.q] - free-text search
   * @param {boolean|null} [filters.is_activated]
   * @param {number} [filters.limit=50]
   * @param {number} [filters.offset=0]
   * @returns {Promise<object>}
   */
  async listMcsCustomers({ site_id = null, q = null, is_activated = null, limit = 50, offset = 0 } = {}) {
    return this.request(`api/v1/admin/mcs-customers${this._buildQuery({ site_id, q, is_activated, limit, offset })}`);
  }

  /**
   * Get a single MCS customer by ID.
   * @param {string|number} id
   * @returns {Promise<object>}
   */
  async getMcsCustomer(id) {
    return this.request(`api/v1/admin/mcs-customers/${id}`);
  }

  /**
   * Manually add an MCS customer not present in the catalog.
   * @param {object} payload - { site_id, company_id, customer_no, customer_name }
   * @returns {Promise<object>}
   */
  async addManualMcsCustomer(payload) {
    return this.request('api/v1/admin/mcs-customers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Update an MCS customer record.
   * @param {string|number} id
   * @param {object} patch - { fuel_type_override?, sync_enabled? }
   * @returns {Promise<object>}
   */
  async updateMcsCustomer(id, patch) {
    return this.request(`api/v1/admin/mcs-customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  /**
   * List the canonical fuel types (for the per-customer fuel-type dropdown).
   * @returns {Promise<{data: Array<{key, label, emission_factor, savings_emission_factor}>}>}
   */
  async listFuelTypes() {
    return this.request('api/v1/admin/fuel-types');
  }

  /**
   * Get the sync status for an MCS customer.
   * @param {string|number} id
   * @returns {Promise<object>}
   *
   * NOTE: As of backend PR #157 (CSV-import rearchitecture), the following
   * fields returned by this endpoint are deprecated and no longer meaningful:
   *   - transactions_cached
   *   - latest_cached_datetime
   *   - backfill_progress_pct
   *   - backfill_status
   *   - backfill_started_at
   *   - backfill_completed_at
   * The useful fields are last_sync_datetime and last_sync_status (from the
   * 7-day rolling daily sync). Do not display deprecated fields in the UI.
   */
  async getMcsCustomerSyncStatus(id) {
    return this.request(`api/v1/admin/mcs-customers/${id}/sync-status`);
  }

  /**
   * Upload a Card Transactions CSV for bulk import into a site's
   * transactions cache. File is multipart/form-data; Authorization header
   * is attached directly — Content-Type must NOT be set manually so that
   * fetch() sets it with the correct multipart boundary when body is FormData.
   *
   * @param {number} siteId
   * @param {File} file - browser File object from an <input type="file">
   * @returns {Promise<{rows_processed: number, rows_written: number, rows_skipped: number, duration_ms: number}>}
   */
  async importTransactions(siteId, file) {
    const fd = new FormData();
    fd.append('file', file);
    // We bypass this.request() here because _authHeaders() forces
    // Content-Type: application/json. For multipart we need only the
    // Authorization header — the browser sets the correct boundary automatically.
    const { getAuth } = await import('firebase/auth');
    const user = getAuth().currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    const url = `${this.baseUrl}api/v1/admin/sites/${siteId}/import-transactions`;
    const resp = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      throw new ApiError(
        errorData.detail || errorData.message || `HTTP Error: ${resp.status}`,
        resp.status,
        errorData
      );
    }
    // 202 Accepted — returns {job_id, status, status_url, filename, ...}
    return resp.json();
  }

  /**
   * Poll a single background import job.
   * @param {number} jobId
   * @returns {Promise<object>} {id, status, rows_processed, rows_written,
   *   rows_skipped, catalog_customers_added, started_at, completed_at,
   *   error_detail, ...}
   */
  async getImportJob(jobId) {
    return this.request(`api/v1/admin/import-jobs/${jobId}`);
  }

  /**
   * List historical import jobs.
   * @param {{site_id?: number, status?: string, limit?: number, offset?: number}} params
   */
  async listImportJobs(params = {}) {
    const q = this._buildQuery(params);
    return this.request(`api/v1/admin/import-jobs${q}`);
  }

  // ==================== MCS — User ↔ Customer Assignment ====================

  /**
   * Get MCS customers assigned to a given user.
   * @param {string} uid - Firebase UID
   * @returns {Promise<object>}
   */
  async getUserMcsCustomers(uid) {
    return this.request(`api/v1/admin/users/${uid}/mcs-customers`);
  }

  /**
   * Replace the set of MCS customers assigned to a given user.
   * @param {string} uid - Firebase UID
   * @param {Array<string|number>} mcs_customer_ids
   * @returns {Promise<object>}
   */
  async updateUserMcsCustomers(uid, mcs_customer_ids) {
    return this.request(`api/v1/admin/users/${uid}/mcs-customers`, {
      method: 'PUT',
      body: JSON.stringify({ mcs_customer_ids }),
    });
  }

  // ==================== MCS — Sync Health + Log ====================

  /**
   * Get overall MCS sync health metrics.
   * @returns {Promise<object>}
   */
  async getSyncHealth() {
    return this.request('api/v1/admin/health/sync');
  }

  /**
   * List sync log entries with optional filters.
   * @param {object} [filters]
   * @param {string|null} [filters.run_type]
   * @param {string|number|null} [filters.site_id]
   * @param {string|number|null} [filters.mcs_customer_id]
   * @param {string|null} [filters.status]
   * @param {number} [filters.limit=50]
   * @param {number} [filters.offset=0]
   * @returns {Promise<object>}
   */
  async listSyncLog({ run_type = null, site_id = null, mcs_customer_id = null, status = null, limit = 50, offset = 0 } = {}) {
    return this.request(`api/v1/admin/sync-log${this._buildQuery({ run_type, site_id, mcs_customer_id, status, limit, offset })}`);
  }

  /**
   * Reconcile user ↔ MCS customer assignments.
   * @param {object} [opts]
   * @param {boolean} [opts.dry_run=false] — preview deltas without writing to Firestore
   * @returns {Promise<object>}
   */
  async reconcileUserAssignments({ dry_run = false } = {}) {
    const qs = dry_run ? '?dry_run=true' : '';
    return this.request(`api/v1/admin/sync/reconcile${qs}`, {
      method: 'POST',
    });
  }

  // ==================== Customer API Keys ====================

  /**
   * List customer API keys (admin).
   * @param {{mcs_customer_id?: number, is_active?: boolean, limit?: number, offset?: number}} [params]
   * @returns {Promise<{data: Array, meta: {total:number, limit:number, offset:number}}>}
   */
  async listApiKeys({ mcs_customer_id = null, is_active = null, limit = 50, offset = 0 } = {}) {
    return this.request(`api/v1/admin/api-keys${this._buildQuery({ mcs_customer_id, is_active, limit, offset })}`);
  }

  /** Get one API key (no plaintext). @param {number} id */
  async getApiKey(id) {
    return this.request(`api/v1/admin/api-keys/${id}`);
  }

  /**
   * Create an API key. Returns plaintext `api_key` ONCE.
   * @param {{mcs_customer_id:number, label:string, daily_limit?:number, burst_limit?:number, expires_at?:string}} payload
   */
  async createApiKey(payload) {
    return this.request('api/v1/admin/api-keys', { method: 'POST', body: JSON.stringify(payload) });
  }

  /** Update an API key. @param {number} id @param {object} patch */
  async updateApiKey(id, patch) {
    return this.request(`api/v1/admin/api-keys/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  /** Rotate an API key — returns a new plaintext `api_key` ONCE. @param {number} id */
  async rotateApiKey(id) {
    return this.request(`api/v1/admin/api-keys/${id}/rotate`, { method: 'POST' });
  }

  /**
   * Permanently delete an API key.
   * @param {number} id
   * @returns {Promise<null>} — resolves to null on 204 No Content
   *
   * NOTE: This bypasses this.request() because the backend returns 204 with no
   * body, and this.request() unconditionally calls response.json() which would
   * throw on an empty body. Pattern follows importTransactions().
   */
  async deleteApiKey(id) {
    const url = `${this.baseUrl}api/v1/admin/api-keys/${id}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await this._authHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.detail || errorData.message || `HTTP Error: ${response.status}`,
        response.status,
        errorData
      );
    }
    // 204 No Content — return null rather than attempting response.json()
    return null;
  }
}

export { AdminApiService, ApiError };
